import Highcharts from "highcharts";

// special_multi_line — a polished, visually rich multi-series percentage trend.
// Rendered through the registry/ChartFactory pipeline (fillHeight) like `line`
// and `multi_line`, so it inherits the same container-sizing/reflow behaviour
// that keeps the axes correct on the live dashboard.

type Point = { x: string; y: number | null };
type Series = { name: string; data: Point[] };

// Semantic mapping (approval = good, denial = bad, abandonment = warning),
// CVD-validated on the light surface. Unknown series names fall back to a
// fixed-order pool so any multi-line payload still renders cleanly.
const SEMANTIC: Array<{ test: RegExp; color: string }> = [
  { test: /approv/i, color: "#16A34A" },
  { test: /deni/i, color: "#DC2626" },
  { test: /abandon/i, color: "#D97706" },
];
const FALLBACK = [
  "#2563EB",
  "#7C3AED",
  "#0D9488",
  "#DB2777",
  "#0EA5E9",
  "#65A30D",
];

const colorForSeries = (name: string, index: number): string => {
  const hit = SEMANTIC.find((s) => s.test.test(name));
  if (hit) return hit.color;
  return FALLBACK[index % FALLBACK.length] ?? "#64748B";
};

const hexToRgba = (hex: string, alpha: number): string => {
  const n = hex.replace("#", "");
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// "2026-W24" → "W24"; "2026-06-08" → "Jun 8"; else as-is.
const formatX = (raw: string): string => {
  const wk = raw.match(/^\d{4}-W(\d+)$/i);
  if (wk) return `W${wk[1]}`;
  const day = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (day) {
    const d = new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return raw;
};

// Accepts the bare series array (registry path) or the {data, metadata} envelope.
const toSeries = (data: unknown): Series[] => {
  if (Array.isArray(data)) return data as Series[];
  const arr = (data as { data?: unknown })?.data;
  return Array.isArray(arr) ? (arr as Series[]) : [];
};

export const renderSpecialMultiLine = (
  data: unknown,
  _spec: unknown,
  _title: string,
): Highcharts.Options => {
  const series = toSeries(data);

  // Canonical x order from the first series (the API sends aligned, sorted x).
  const baseX = (series[0]?.data ?? []).map((p) => p.x);

  // Trim a trailing run where every series is 0 (a current, not-yet-populated
  // period) so the lines span the full width instead of plunging to zero.
  let realCount = baseX.length;
  for (let i = baseX.length - 1; i >= 0; i--) {
    const allZero = series.every((s) => (s.data[i]?.y ?? 0) === 0);
    if (allZero) realCount = i;
    else break;
  }
  if (realCount === 0) realCount = baseX.length; // never blank everything

  const categories = baseX.slice(0, realCount).map(formatX);

  const hcSeries: Highcharts.SeriesAreasplineOptions[] = series.map((s, i) => {
    const color = colorForSeries(s.name, i);
    const values = baseX
      .slice(0, realCount)
      .map((_, idx) => s.data[idx]?.y ?? null);
    return {
      type: "areaspline",
      name: s.name,
      color,
      data: values,
      lineWidth: 2.75,
      connectNulls: false,
      // Soft glow beneath each line for depth.
      shadow: {
        color: hexToRgba(color, 0.35),
        offsetX: 0,
        offsetY: 3,
        width: 5,
        opacity: 0.5,
      } as unknown as Highcharts.ShadowOptionsObject,
      // Subtle gradient fill, fading to transparent so overlapping bands stay legible.
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, hexToRgba(color, 0.16)],
          [1, hexToRgba(color, 0)],
        ],
      } as unknown as Highcharts.GradientColorObject,
      marker: {
        enabled: false,
        symbol: "circle",
        states: {
          hover: {
            enabled: true,
            radius: 5,
            fillColor: color,
            lineColor: "#ffffff",
            lineWidth: 2,
          },
        },
      },
      states: {
        hover: { lineWidthPlus: 1, halo: { size: 9, opacity: 0.12 } },
        inactive: { opacity: 0.25 },
      },
    };
  });

  return {
    chart: {
      type: "areaspline",
      backgroundColor: "transparent",
      spacing: [12, 8, 8, 6],
      style: { fontFamily: "inherit" },
    },
    title: { text: "" },
    credits: { enabled: false },
    accessibility: { enabled: false },
    // Match the other charts (LineRenderer): tuck the context button up out of
    // the plot so it's driven by the card-header hamburger, not shown in-chart.
    // ChartFactory merges the registry's exportItems into this button.
    exporting: {
      enabled: true,
      fallbackToExportServer: true,
      sourceWidth: 1400,
      sourceHeight: 600,
      scale: 2,
      buttons: {
        contextButton: {
          align: "right",
          verticalAlign: "top",
          y: -43,
          x: -30,
        },
      },
    },
    legend: {
      align: "left",
      verticalAlign: "top",
      margin: 14,
      symbolRadius: 6,
      symbolHeight: 9,
      symbolWidth: 9,
      itemStyle: {
        fontSize: "12px",
        fontWeight: "600",
        color: "#475569",
      },
      itemHoverStyle: { color: "#0f172a" },
      itemHiddenStyle: { color: "#cbd5e1" },
    },
    xAxis: {
      categories,
      tickLength: 0,
      // Span the plot edge-to-edge: first point at the left, last at the right.
      minPadding: 0,
      maxPadding: 0,
      startOnTick: false,
      endOnTick: false,
      tickmarkPlacement: "on",
      lineColor: "#e6e8ec",
      labels: {
        style: { color: "#94a3b8", fontSize: "11px", fontWeight: "500" },
      },
      crosshair: { width: 1, color: "rgba(15,23,42,0.12)", dashStyle: "Solid" },
    },
    yAxis: {
      title: { text: undefined },
      min: 0,
      gridLineColor: "rgba(148,163,184,0.18)",
      gridLineDashStyle: "Dash",
      labels: {
        style: { color: "#94a3b8", fontSize: "11px" },
        formatter() {
          return `${this.value}%`;
        },
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      outside: true,
      backgroundColor: "#0f172a",
      borderWidth: 0,
      borderRadius: 10,
      shadow: false,
      padding: 0,
      style: { color: "#f8fafc" },
      formatter() {
        const ctx = this as unknown as {
          x: string;
          points?: Array<{
            y: number;
            series: { name: string; color: string };
          }>;
        };
        const rows = [...(ctx.points ?? [])]
          .sort((a, b) => b.y - a.y)
          .map(
            (p) => `
            <div style="display:flex;align-items:center;gap:7px;padding:2px 0;">
              <span style="width:8px;height:8px;border-radius:50%;background:${p.series.color};flex-shrink:0;"></span>
              <span style="font-size:11.5px;color:#cbd5e1;margin-right:auto;">${p.series.name}</span>
              <span style="font-size:12px;font-weight:700;color:#f8fafc;">${p.y}%</span>
            </div>`,
          )
          .join("");
        return `<div style="padding:8px 10px;min-width:150px;">
          ${rows}
        </div>`;
      },
    },
    plotOptions: {
      areaspline: {
        linecap: "round",
        marker: { enabled: false },
        states: { inactive: { opacity: 0.25 } },
      },
    },
    series: hcSeries,
  };
};
